/**
 * PendingInsertStore — in-memory store for editor-local UNDEFINED insertions.
 *
 * Phase FB.5 of docs/design/frontend-mv-architecture-260524.md: relocated
 * from controllers/insert-flow.js to store/ per the Q2 ruling — the class
 * IS a Flux store (state-bearing Map + subscribe semantics), not a flow
 * controller. The layer-import lint flagged editor-state.ts (store) →
 * insert-flow.js (controllers) as a violation; the fix is to put the
 * state surface where it belongs.
 *
 * Tracks editor-local UNDEFINED insertions. Lives only in memory — no
 * IR or backend op fires until `resolvePendingType()` is called.
 *
 * Save-block invariant: while `size() > 0`, Save is blocked. Subscribers
 * receive notifications on any change.
 *
 * Layer: store. Allowed imports: util only.
 *
 * @module store/pending-insert
 */
import { log } from "../log.js";
/**
 * Tracks editor-local UNDEFINED insertions with subscribe semantics.
 *
 * Construction is cheap (empty Map + empty listener Set). `resetEditorState`
 * (in controllers/empty-state) replaces the singleton wholesale rather
 * than calling `clear()` so save-block subscribers re-bind to the fresh
 * instance — that pattern remains valid post-relocation.
 */
export class PendingInsertStore {
    _map;
    _listeners;
    constructor() {
        this._map = new Map();
        this._listeners = new Set();
    }
    add(entry) {
        if (!entry?.editorId)
            throw new Error("PendingInsertStore: editorId required");
        this._map.set(entry.editorId, entry);
        this._notify();
    }
    remove(editorId) {
        const had = this._map.delete(editorId);
        if (had)
            this._notify();
        return had;
    }
    get(editorId) {
        return this._map.get(editorId) ?? null;
    }
    has(editorId) {
        return this._map.has(editorId);
    }
    size() {
        return this._map.size;
    }
    /** All pending inserts in insertion order. */
    list() {
        return Array.from(this._map.values());
    }
    /** Whether ANY pending insert exists — Save-block predicate. */
    hasPending() {
        return this._map.size > 0;
    }
    subscribe(fn) {
        if (typeof fn !== "function")
            return () => { };
        this._listeners.add(fn);
        return () => { this._listeners.delete(fn); };
    }
    _notify() {
        for (const fn of Array.from(this._listeners)) {
            try {
                fn(this);
            }
            catch (err) {
                // Listener throws don't have a recovery path inside the store —
                // a single broken subscriber would block the rest. We log and
                // continue so the remaining listeners still see the change.
                //
                // User impact: a broken view (e.g. the Save-button gating, the
                // pin-list rendering, the highlight overlay) may briefly show
                // stale state for one frame. The next user action — pointer
                // move, click, keyboard nav, or another insert/remove — will
                // re-emit a fresh _notify() and resync. We don't retry the
                // failed listener inline because any side-effect it already
                // performed before throwing would re-fire, and a partial
                // side-effect re-applied is strictly worse than a one-frame
                // desync that self-heals on the next gesture.
                log.warn("[pending-insert] listener threw during notify; continuing:", err);
            }
        }
    }
}
